import { PortableText } from '@portabletext/react'
import { BUTTON_SIZE, BUTTON_TYPE, DSBottomSheet, DSButton, DSModal, Switch } from '@repo/design-system/index'
import { QIcon_cart, QIcon_chevron_bold_right, QIcon_chevron_right, QIcon_mail } from '@repo/icons/index'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { useEventTracking } from '../../../helpers/gtmHelper'
import { useAITutorPermission } from '../../../modules/apis/student/ai-tutor'
import { usePoints, useRemainingCreditsCount } from '../../../modules/apis/student/credit'
import { useCustomerType } from '../../../modules/apis/student/products'
import { useCurrentUser, useIsPromotionUser } from '../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import useHandleLogout from '../../../modules/hooks/student/auth/useHandleLogout'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import {
  n_account,
  n_buy,
  n_certificate,
  n_common,
  n_coupon,
  n_guide,
  n_invite,
  n_menu,
  n_mycredit,
  n_point,
  n_pricing,
  n_subscribe,
  n_updates,
} from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import urls from '../../../modules/urls'
import {
  MATH_METHOD,
  convertPriceWithCountryCode,
  currencyFormat,
  formatNumberAsCurrency,
  isMulticampus,
} from '../../../modules/utils'
import { AI_TUTOR_ROLEPLAY, COLORS, COUNTRY_ALPHA2_CODE } from '../../../modules/vars'
import { fetchProfile } from '../../../pages/[lang]/portal/system/banner'
import useUserStore from '../../../store/useUserStore'
import DeadlineComponent from '../../common/DeadlineComponent'
import { ptComponents } from '../../sanity/util'
import SectionCredit from '../home/SectionCredit'
import SectionMembership from '../home/SectionMembership'

const PurchaseBottomSheet = ({ isOpen, setIsOpen }) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const getFullUrl = useReturnFullUrl()

  const onClickLessonPurchase = () => {
    router.push(`${urls.student.newPortal.pricing.index}`, {
      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
      appSendData: {
        url: getFullUrl(`${urls.student.portal.pricing.index}?app=true`),
      },
    })
  }

  const onClickAITutorPurchase = () => {
    router.push(urls.webview.aiTutor.purchase, {
      appScreenName: APP_SCREEN_NAME.IAP_SCREEN,
      appSendData: { url: getFullUrl(urls.webview.aiTutor.purchase) },
    })
  }

  return (
    <DSBottomSheet
      id="TextOnlySMTwoButtonHorizontalPopup"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      closeButton={true}
      onClose={() => setIsOpen(false)}
    >
      <DSModal.Title className="pt-[6px]">
        <span className="Title2-18Bd px-[20px]">{t(n_pricing.bottom.select)}</span>
      </DSModal.Title>
      <DSModal.Content className="mt-[8px] p-[20px] mb-[16px]">
        <DSButton
          size={BUTTON_SIZE.SM}
          type={BUTTON_TYPE.SOLID}
          customBgColor={COLORS.WHITE}
          customBorderColor={COLORS.GRAY100}
          className="border-1 !px-[16px] !py-[24px]"
          onClick={onClickLessonPurchase}
        >
          <DSButton.Label className="flex flex-row items-center justify-between gap-[4px] w-full">
            <span className="text-black Body-16Bd">{t(n_subscribe.tab.one_on_one)}</span>
            <QIcon_chevron_bold_right width={20} height={20} color={COLORS.GRAY400} />
          </DSButton.Label>
        </DSButton>
        <DSButton
          size={BUTTON_SIZE.SM}
          type={BUTTON_TYPE.SOLID}
          customBgColor={COLORS.WHITE}
          customBorderColor={COLORS.GRAY100}
          className="border-1 !px-[16px] !py-[24px] mt-[8px]"
          onClick={onClickAITutorPurchase}
        >
          <DSButton.Label className="flex flex-row items-center justify-between gap-[4px] w-full">
            <span className="text-black Body-16Bd">{t(n_subscribe.tab.membership)}</span>
            <QIcon_chevron_bold_right width={20} height={20} color={COLORS.GRAY400} />
          </DSButton.Label>
        </DSButton>
      </DSModal.Content>
    </DSBottomSheet>
  )
}

const AccountMenuBody = ({ showMenu, setShowMenu, type = 'window', appVersion = '' }) => {
  const { t, locale, isLocaleKO } = useTranslation()
  const { isPromotionUser, dday } = useIsPromotionUser()
  const isWebview = useIsWebview()
  const getFullUrl = useReturnFullUrl()
  const router = useAppRouter()
  const { sendClickEvent } = useEventTracking()
  const { data: aiTutorMemebershipPermissionData } = useAITutorPermission(AI_TUTOR_ROLEPLAY)

  const { currentUser } = useCurrentUser()
  const currentGeo = useUserStore((state) => state.session.currentGeo)

  const countryCode = currentGeo.country_code
  const { data: creditInfo } = useRemainingCreditsCount()
  const { data: pointInfo } = usePoints()
  const handleLogout = useHandleLogout()
  const [showPopup, setShowPopup] = useState(false)
  const [showWebviewUrl, setShowWebviewUrl] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const { data: customerType } = useCustomerType()
  const isB2CEligible = customerType?.isB2CEligible

  const { data } = useQuery({
    queryKey: ['mktProfileBanner'],
    queryFn: () => fetchProfile(),
    staleTime: 1000 * 60 * 60 * 24, // 24시간
    cacheTime: 1000 * 60 * 60 * 24, // 24시간
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  const getLanguageTitle = (lang) => {
    switch (lang) {
      case 'ko':
        return '한국어'
      case 'kr':
        return '한국어'
      case 'en':
        return 'English'
      case 'zh_chs':
        return '中文（简体）'
      case 'zh_cht':
        return '中文（繁體）'
      case 'ja':
        return '日本語'
      case 'vi':
        return 'Tiếng Việt'
    }
  }
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    window?.ChannelIO?.('hideChannelButton')
    window?.ChannelIO?.('shutdown')
  }, [])

  useEffect(() => {
    if (setShowMenu) {
      const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && event.target instanceof Node && !menuRef.current.contains(event.target)) {
          setShowMenu(false)
        }
      }
      if (showMenu) {
        document.addEventListener('click', handleClickOutside)
      }
      return () => {
        document.removeEventListener('click', handleClickOutside)
      }
    }
  }, [showMenu, setShowMenu])

  const creditCount40 =
    creditInfo?.regular_40_credit_count +
    creditInfo?.makeup_40_credit_count +
    creditInfo?.timelimit_40_credit_count +
    creditInfo?.urgent_40_credit_count
  const creditCount20 =
    creditInfo?.regular_20_credit_count +
    creditInfo?.makeup_20_credit_count +
    creditInfo?.timelimit_20_credit_count +
    creditInfo?.urgent_20_credit_count

  const handleBannerClick = (data) => {
    sendClickEvent({ button_name: 'profile_menu_text_banner' })

    data?.link.includes('http') ? window.open(data?.link, '_blank') : fromBannerToInvite()
  }

  const handlePurchase = () => {
    // router.purchase()
    if (isWebview) {
      setIsOpen(true)
    } else {
      router.push(`${urls.student.newPortal.pricing.index}`, {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: {
          url: getFullUrl(`${urls.student.portal.pricing.index}?app=true`),
        },
      })
    }
  }

  const fromBannerToInvite = () => {
    router.push(urls.student.newPortal.mypage.invite, {
      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
      appSendData: {
        url: getFullUrl(urls.webview.mypage.invite),
      },
    })
  }

  useEffect(() => {
    if (isWebview && window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: showWebviewUrl ? 'SHOW_WEBVIEW_URL' : 'HIDE_WEBVIEW_URL' })
      )
    }
  }, [showWebviewUrl, isWebview])

  if (isMulticampus(currentUser)) {
    return (
      <>
        {/* 계정관리 */}
        <div
          ref={menuRef}
          className={classNames({
            hidden: !showMenu,
            flex: showMenu,
            'absolute right-[0%] top-[60px] z-[120] w-full max-w-[280px] flex-col rounded-[8px] bg-white px-[16px] py-[20px] text-[14px] shadow-[0px_2px_8px_0px_rgba(0,0,0,0.15)]':
              type == 'window',
            'flex flex-col p-[16px] pb-[64px]': type == 'page',
          })}
        >
          <div
            className="cursor-pointer py-[8px] px-[4px] text-gray-700"
            onClick={() =>
              router.push(urls.student.newPortal.mypage.currentCredit(0), {
                appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                appSendData: { url: getFullUrl(urls.webview.mypage.currentCredit(0)) },
              })
            }
          >
            {t(n_mycredit.title)}
          </div>
          <div
            className="cursor-pointer py-[8px] px-[4px] text-gray-700"
            onClick={() =>
              router.push(urls.student.newPortal.mypage.notification, {
                appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                appSendData: { url: getFullUrl(urls.webview.mypage.notification) },
              })
            }
          >
            {t(n_account.tap_3)}
          </div>
          <div>
            <div
              className="flex cursor-pointer items-center justify-between"
              onClick={() =>
                router.push(urls.student.newPortal.mypage.timezone, {
                  appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                  appSendData: { url: getFullUrl(urls.webview.mypage.timezone) },
                })
              }
            >
              <div className="cursor-pointer py-[8px] px-[4px] text-gray-700">{t(n_account.tap_5)}</div>
              <div className="Body2-14Md text-purple-500">{currentUser?.timezone}</div>
            </div>
          </div>
          {type == 'page' && (
            <div>
              <div className="flex items-center justify-between py-[8px] px-[4px]">
                <div className="text-gray-700">{t(n_menu.app_version)}</div>
                <div className="text-gray-200">{appVersion}</div>
              </div>
            </div>
          )}
          <div
            className="flex cursor-pointer items-center justify-between py-[8px] px-[4px]"
            onClick={() =>
              router.push(urls.student.newPortal.mypage.leave, {
                appScreenName: APP_SCREEN_NAME.OUTLINK_SCREEN,
                appSendData: { url: getFullUrl(urls.webview.leaveRingle) },
              })
            }
          >
            <div className="text-gray-700">{t(n_menu.leave)}</div>
          </div>
          <div className="my-[8px]">
            <div
              className="cursor-pointer py-[8px] px-[4px] text-gray-700"
              onClick={() => (isWebview ? setShowPopup(true) : handleLogout())}
            >
              {t(n_account.logout)}
            </div>
          </div>
        </div>
        <DSModal id="TextOnlySMTwoButtonHorizontalPopup" isOpen={showPopup} setIsOpen={setShowPopup} size="sm">
          <DSModal.Title>{t(n_menu.logout_modal)}</DSModal.Title>
          <DSModal.ButtonContainer direction="horizontal">
            <DSButton
              size={BUTTON_SIZE.MD}
              type={BUTTON_TYPE.OUTLINE}
              customBgColor={COLORS.WHITE}
              onClick={() => setShowPopup(false)}
            >
              <DSButton.Label>{t(n_common.btn_cancel)}</DSButton.Label>
            </DSButton>
            <DSButton size={BUTTON_SIZE.MD} onClick={() => router.logout()}>
              <DSButton.Label>{t(n_common.btn_ok)}</DSButton.Label>
            </DSButton>
          </DSModal.ButtonContainer>
        </DSModal>
      </>
    )
  }

  return (
    <>
      {/* 계정관리 */}
      <div
        ref={menuRef}
        className={classNames('Body1-16Md', {
          hidden: !showMenu,
          flex: showMenu,
          'absolute right-[0%] top-[60px] z-[120] w-full max-w-[280px] flex-col rounded-[8px] bg-white px-[16px] py-[20px] text-[14px] shadow-[0px_2px_8px_0px_rgba(0,0,0,0.15)]':
            type == 'window',
          'bg-white flex flex-col p-[16px] pb-[64px]': type == 'page',
        })}
      >
        <div className="flex w-full flex-col pb-[12px]">
          {/* 프로필 이미지 / 이메일 */}
          <div className="flex">
            {type == 'page' && (
              <div className="mr-[12px]">
                <img
                  src={
                    currentUser?.image_url ||
                    'https://d38emex6h5e12i.cloudfront.net/2024-image/1718846955842_imagePersonRectangle.png'
                  }
                  className="h-[60px] w-[60px] rounded-full"
                  alt="user_profile_image"
                />
              </div>
            )}
            <div>
              <div className="Body1-16Bd text-black">{currentUser?.profile?.name}</div>
              <div className="Body2-14Md mt-[4px] overflow-hidden text-ellipsis whitespace-nowrap text-gray-500">
                {currentUser?.email}
              </div>
            </div>
          </div>
          {/* 텍스트 배너 영역 */}
          {data && isB2CEligible && (
            <div
              onClick={() => handleBannerClick(data)}
              className="Body2-14Md mt-[20px] flex cursor-pointer flex-row items-center gap-[8px] rounded-[8px] bg-gray-100 p-[12px]"
            >
              <PortableText
                value={isLocaleKO ? data.ko_profile.content : data.en_profile.content}
                components={ptComponents}
              />
              <div className="h-[20px] w-[20px]">
                <QIcon_chevron_right width={20} height={20} color={COLORS.GRAY500} />
              </div>
            </div>
          )}
        </div>
        {type == 'page' && (
          <div
            className={classNames(
              'mt-[20px] mb-[24px] grid gap-x-[8px]',
              isB2CEligible ? 'grid-cols-4' : 'grid-cols-3'
            )}
          >
            {/* 구매 */}
            <div className="relative flex cursor-pointer flex-col items-center justify-center" onClick={handlePurchase}>
              <div className="relative">
                <QIcon_cart width={24} height={24} />
                {isPromotionUser && (
                  <div className="Overline-10Bd absolute top-[-20px] left-[10px] inline-block rounded-[4px] bg-red-500 px-[6px] py-[2px]">
                    <span className="whitespace-nowrap text-white">
                      <DeadlineComponent dday={dday} />
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-[8px] text-gray-700">{t(n_buy.title)}</div>
            </div>
            {/* 친구초대 */}
            {isB2CEligible && (
              <div
                className="flex cursor-pointer flex-col items-center justify-center"
                onClick={() =>
                  router.push(urls.student.newPortal.mypage.invite, {
                    appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                    appSendData: {
                      url: getFullUrl(urls.webview.mypage.invite),
                    },
                  })
                }
              >
                <QIcon_mail width={24} height={24} />
                <div className="mt-[8px] text-gray-700">{t(n_invite.title)}</div>
              </div>
            )}
            {/* FAQ */}
            <div
              className="flex cursor-pointer flex-col items-center justify-center"
              onClick={() => {
                router.push(urls.student.newPortal.helpCenter.index + '#1', {
                  appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                  appSendData: {
                    url: getFullUrl(urls.webview.helpCenter.index) + '#1',
                  },
                })
              }}
            >
              <img
                src="https://d38emex6h5e12i.cloudfront.net/2024-image/1722846385524_faq-hover.webp"
                alt="faq"
                className="w-[24px] h-[24px]"
              />
              <div className="mt-[8px] text-gray-700">{t(n_common.label_faq)}</div>
            </div>
            <div
              className="flex cursor-pointer flex-col items-center justify-center"
              onClick={() =>
                router.push(urls.student.newPortal.helpCenter.index + '#0', {
                  appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                  appSendData: {
                    url: getFullUrl(urls.webview.helpCenter.index) + '#0',
                  },
                })
              }
            >
              <img
                src="https://d38emex6h5e12i.cloudfront.net/2024-image/1722846428273_guide-hover.webp"
                alt="faq"
                className="w-[24px] h-[24px]"
              />
              <div className="mt-[8px] text-gray-700">{t(n_guide.page_title)}</div>
            </div>
          </div>
        )}
        <hr className="hidden border-gray-200 sm:block my-8" />
        <div className="h-[8px] w-full border-t border-gray-100 bg-gray-50 sm:hidden"></div>

        {/* 내수업권 */}
        <div className=" smd:gap-y-[4px] flex flex-col">
          {creditInfo && (
            <div
              className="py-8 flex cursor-pointer items-center justify-between"
              onClick={(e) =>
                router.push(urls.student.newPortal.mypage.currentCredit(0), {
                  appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                  appSendData: {
                    url: getFullUrl(urls.webview.mypage.currentCredit(0)),
                  },
                })
              }
            >
              <div>
                <div className="px-[4px] text-gray-700">{t(n_subscribe.tab.one_on_one)}</div>
              </div>
              <div className="flex items-center">
                <div className="Body2-14Md flex">
                  <div className="text-gray-500">{t(n_common.label_40min)}</div>
                  <div className="ml-[4px] text-purple-500">{creditCount40}</div>
                </div>
                <div className="Body2-14Md ml-[20px] flex">
                  <div className="text-gray-500">{t(n_common.label_20min)}</div>
                  <div className="ml-[4px] text-purple-500">{creditCount20}</div>
                </div>
                <div className="ml-[3px]">
                  <QIcon_chevron_right color={COLORS.GRAY500} />
                </div>
              </div>
            </div>
          )}
          {type == 'page' && <SectionCredit />}

          {/* AI 스피킹 & AI 튜터 */}
          <div
            className="flex cursor-pointer items-center justify-between"
            onClick={(e) =>
              router.push(urls.student.newPortal.mypage.currentCredit(1), {
                appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                appSendData: {
                  url: getFullUrl(urls.webview.mypage.currentCredit(1)),
                },
              })
            }
          >
            <div>
              <div className="py-[8px] px-[4px] text-gray-700">{t(n_subscribe.tab.membership)}</div>
            </div>
            {
              <div className="flex flex-row items-center">
                <div className="ml-[3px]">
                  <QIcon_chevron_right color={COLORS.GRAY500} />
                </div>
              </div>
            }
          </div>

          {(aiTutorMemebershipPermissionData?.current || aiTutorMemebershipPermissionData?.temp_membership) &&
            isWebview &&
            isB2CEligible && (
              <div className="">
                <SectionMembership />
              </div>
            )}

          {pointInfo && (
            <div
              className="flex cursor-pointer items-center justify-between"
              onClick={() =>
                router.push(urls.student.newPortal.mypage.point, {
                  appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                  appSendData: { url: getFullUrl(urls.webview.mypage.point) },
                })
              }
            >
              <div>
                <div className="py-[8px] px-[4px] text-gray-700">{t(n_point.title)}</div>
              </div>
              <div className="flex items-center">
                <div className="Body2-14Md flex">
                  <div className="text-purple-500">
                    {countryCode === COUNTRY_ALPHA2_CODE.KR
                      ? `${currencyFormat(pointInfo.summary?.total_point?.value)}P`
                      : `${formatNumberAsCurrency(
                          convertPriceWithCountryCode(
                            pointInfo.summary?.total_point?.value || 0,
                            countryCode,
                            true,
                            MATH_METHOD.FLOOR
                          ),
                          countryCode,
                          locale
                        )}`}
                  </div>
                </div>
                <div className="ml-[3px]">
                  <QIcon_chevron_right color={COLORS.GRAY500} />
                </div>
              </div>
            </div>
          )}
          <div
            className="flex cursor-pointer items-center justify-between"
            onClick={() =>
              router.push(urls.student.newPortal.mypage.couponBox, {
                appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                appSendData: { url: getFullUrl(urls.webview.mypage.couponBox) },
              })
            }
          >
            <div className="">
              <div className="py-[8px] px-[4px] text-gray-700 relative">{t(n_coupon.title)}</div>
            </div>
            <div className="flex items-center">
              <div className="Body2-14Md flex">
                <div className="text-purple-500">{currentUser?.discount_coupons?.length}</div>
              </div>
              <div className="ml-[3px]">
                <QIcon_chevron_right color={COLORS.GRAY500} />
              </div>
            </div>
          </div>
          {/* 친구초대 */}
          {/* {type == 'window' && (
            <div
              className="cursor-pointer"
              onClick={() =>
                router.push(urls.student.newPortal.mypage.invite, {
                  appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                  appSendData: { url: getFullUrl(urls.webview.mypage.invite) },
                })
              }
            >
              <div className="py-[8px] px-[4px] text-gray-700">{t(n_invite.title)}</div>
            </div>
          )} */}
        </div>

        <hr className="my-[8px] border-gray-200" />

        <div className="mt-[1px] Body1-16Md ">
          {/* 기본정보 */}
          <div
            className="cursor-pointer py-[8px] px-[4px] text-gray-700"
            onClick={() =>
              router.push(urls.student.newPortal.mypage.basic, {
                appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                appSendData: { url: getFullUrl(urls.webview.mypage.basic) },
              })
            }
          >
            {/* 계정 관리 */}
            {t(n_account.page_title)}
          </div>

          {isWebview && (
            <>
              <div
                className="cursor-pointer py-[8px] px-[4px] text-gray-700"
                onClick={() =>
                  router.push(urls.student.newPortal.mypage.profile, {
                    appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                    appSendData: { url: getFullUrl(urls.webview.mypage.profile) },
                  })
                }
              >
                {t(n_account.tap_2)}
              </div>
              {/* 알림 수신 설정 */}
              <div
                className="cursor-pointer py-[8px] px-[4px] text-gray-700"
                onClick={() =>
                  router.push(urls.student.newPortal.mypage.notification, {
                    appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                    appSendData: { url: getFullUrl(urls.webview.mypage.notification) },
                  })
                }
              >
                {t(n_account.tap_3)}
              </div>
              {/* 타임존 변경 */}
              <div
                className="flex cursor-pointer items-center justify-between"
                onClick={() =>
                  router.push(urls.student.newPortal.mypage.timezone, {
                    appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                    appSendData: { url: getFullUrl(urls.webview.mypage.timezone) },
                  })
                }
              >
                <div className="cursor-pointer py-[8px] px-[4px] text-gray-700">{t(n_account.tap_5)}</div>
                <div className="Body2-14Md text-purple-500">{currentUser?.timezone}</div>
              </div>
              {/* 언어 변경 */}
              <div
                className="flex cursor-pointer items-center justify-between"
                onClick={() =>
                  router.push(urls.student.newPortal.mypage.language, {
                    appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                    appSendData: { url: getFullUrl(urls.webview.mypage.language) },
                  })
                }
              >
                <div className="cursor-pointer py-[8px] px-[4px] text-gray-700">{t(n_account.tap_6)}</div>
                <div className="Body2-14Md text-purple-500">{getLanguageTitle(currentUser?.locale)}</div>
              </div>
              {/* 비밀번호 변경 */}
              <div
                className="flex items-center justify-between"
                onClick={() =>
                  router.push(urls.student.newPortal.mypage.password, {
                    appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                    appSendData: { url: getFullUrl(urls.webview.mypage.password) },
                  })
                }
              >
                <div className="cursor-pointer py-[8px] px-[4px] text-gray-700">{t(n_account.tap_4)}</div>
              </div>
            </>
          )}

          <hr className="my-[8px] border-gray-200" />
        </div>

        {/* 공지사항 */}
        <div className="mt-[1px]">
          <div
            className="cursor-pointer py-[8px] px-[4px] "
            onClick={() => {
              sendClickEvent({ button_name: 'profile_menu_invite' })
              router.push(urls.student.newPortal.notice.index, {
                appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                appSendData: {
                  url: getFullUrl(urls.webview.notice.index),
                },
              })
            }}
          >
            <div className="text-gray-700 flex relative">
              <div className="relative">{t(n_updates.title)}</div>
            </div>
          </div>
        </div>
        {/* 증명서 발급 */}
        {!isWebview && (
          <div>
            <div
              className="cursor-pointer py-[8px] px-[4px] "
              onClick={() =>
                router.push(urls.student.newPortal.mypage.currentCredit(0), {
                  appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                  appSendData: {
                    url: getFullUrl(urls.webview.mypage.currentCredit(0)),
                  },
                })
              }
            >
              <div className="text-gray-700 Body1-16Md">{t(n_certificate.page_title)}</div>
            </div>
          </div>
        )}
        {type == 'page' && (
          <div className="Body1-16Md">
            {/* 개인정보 처리 방침 */}
            <div
              className="cursor-pointer py-[8px] px-[4px] text-gray-700"
              onClick={() =>
                router.push(urls.student.newPortal.policy.privacy, {
                  appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                  appSendData: { url: getFullUrl(urls.webview.policy.privacy) },
                })
              }
            >
              {t(n_menu.privacy)}
            </div>
            <div
              className="flex cursor-pointer items-center justify-between py-[8px] px-[4px]"
              onClick={() =>
                router.push(urls.student.newPortal.policy.terms, {
                  appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                  appSendData: { url: getFullUrl(urls.webview.policy.terms) },
                })
              }
            >
              <div className="text-gray-700">{t(n_menu.terms)}</div>
            </div>
            <div className="flex items-center justify-between py-[8px] px-[4px]">
              <div className="text-gray-700">{t(n_menu.app_version)}</div>
              <div className="text-gray-200">{appVersion}</div>
            </div>
            <div
              className="flex cursor-pointer items-center justify-between py-[8px] px-[4px]"
              onClick={() =>
                router.push(urls.student.newPortal.mypage.leave, {
                  appScreenName: APP_SCREEN_NAME.OUTLINK_SCREEN,
                  appSendData: { url: getFullUrl(urls.webview.leaveRingle) },
                })
              }
            >
              <div className="text-gray-700">{t(n_menu.leave)}</div>
            </div>
            {currentUser?.is_ringle_member && (
              <div className="flex cursor-pointer items-center justify-between py-[8px] px-[4px]">
                <div className="text-gray-700">웹뷰 URL 보기</div>
                <Switch checked={showWebviewUrl} setChecked={() => setShowWebviewUrl(!showWebviewUrl)} />
              </div>
            )}
          </div>
        )}
        <hr className="mt-[8px] border-gray-200" />
        <div className="my-[8px]">
          <div
            className="cursor-pointer py-[8px] px-[4px] text-gray-700"
            onClick={() => (isWebview ? setShowPopup(true) : handleLogout())}
          >
            {t(n_account.logout)}
          </div>
        </div>
      </div>
      <DSModal id="TextOnlySMTwoButtonHorizontalPopup" isOpen={showPopup} setIsOpen={setShowPopup} size="sm">
        <DSModal.Title>{t(n_menu.logout_modal)}</DSModal.Title>
        <DSModal.ButtonContainer direction="horizontal">
          <DSButton
            size={BUTTON_SIZE.MD}
            type={BUTTON_TYPE.OUTLINE}
            customBgColor={COLORS.WHITE}
            onClick={() => setShowPopup(false)}
          >
            <DSButton.Label>{t(n_common.btn_cancel)}</DSButton.Label>
          </DSButton>
          <DSButton size={BUTTON_SIZE.MD} onClick={() => router.logout()}>
            <DSButton.Label>{t(n_common.btn_ok)}</DSButton.Label>
          </DSButton>
        </DSModal.ButtonContainer>
      </DSModal>

      <PurchaseBottomSheet isOpen={isOpen} setIsOpen={setIsOpen} />
    </>
  )
}

export default AccountMenuBody
